import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { crossBorderOrderService } from "../../../service/admin/crossBorderOrderService";
import { ProductsApiService } from "../../../service/website/productsApiService";
import ExcelTable from "../../../component/library/excelTable";
import Pagination from "../../../component/library/pagination";
import "./style.css";

export default function CrossBorderOrderManagement() {
  const navigate = useNavigate();
  const [crossBorderOrders, setCrossBorderOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, failedCount: 0, cancelledCount: 0 });
  const [filters, setFilters] = useState({ status: "", search: "" });
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [translatedNames, setTranslatedNames] = useState({});
  const [translatedVariants, setTranslatedVariants] = useState({}); // Used for Excel export
  const [fetchingLogisticsId, setFetchingLogisticsId] = useState(null);
  const [fetchingOrderDataId, setFetchingOrderDataId] = useState(null);
  const [fetchingTraceId, setFetchingTraceId] = useState(null);
  const [isBulkFetching, setIsBulkFetching] = useState(false);
  const [isBulkFetchingOrderDetails, setIsBulkFetchingOrderDetails] = useState(false);
  const [isBulkFetchingTrace, setIsBulkFetchingTrace] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // "all", "failed", or "cancelled"

  const loader = useLoader();
  const message = useMessage();

  // Translate Chinese supplier names to English
  const translateSupplierNames = async (orders) => {
    const chineseNames = {};
    orders.forEach(order => {
      const name = order.supplierName;
      if (name && /[\u4e00-\u9fa5]/.test(name) && !translatedNames[name]) {
        chineseNames[name] = name;
      }
    });

    if (Object.keys(chineseNames).length === 0) return;

    try {
      const translateData = { supplierNames: chineseNames };
      const res = await ProductsApiService.translateMandarinToEnglish(translateData, 'en');
      if (res.status?.code === 200 && res.data?.supplierNames) {
        setTranslatedNames(prev => ({ ...prev, ...res.data.supplierNames }));
      }
    } catch (error) {
      console.error("Translation error:", error);
    }
  };

  // Translate variants to English in the background for Excel Export
  const translateVariants = async (orders) => {
    const chineseVariants = {};
    orders.forEach(order => {
      const orderDetailArray = Array.isArray(order.buyerOrderDetail) ? order.buyerOrderDetail : [order.buyerOrderDetail];
      const firstOrderDetail = orderDetailArray[0]?.result?.result || orderDetailArray[0]?.result || {};
      const productItems = firstOrderDetail?.productItems || [];

      order.items?.forEach(item => {
        const matchingProduct = productItems.find(p => p.specId === item.specId || p.productID === item.offerId);
        const variantCn = matchingProduct?.skuInfos?.map(s => `${s.name}: ${s.value}`).join(', ');
        if (variantCn && /[\u4e00-\u9fa5]/.test(variantCn) && !translatedVariants[variantCn]) {
          chineseVariants[variantCn] = variantCn;
        }
      });
    });

    if (Object.keys(chineseVariants).length === 0) return;

    try {
      const translateData = { supplierNames: chineseVariants }; 
      const res = await ProductsApiService.translateMandarinToEnglish(translateData, 'en');
      if (res.status?.code === 200 && res.data?.supplierNames) {
        setTranslatedVariants(prev => ({ ...prev, ...res.data.supplierNames }));
      }
    } catch (error) {
      console.error("Variant translation error:", error);
    }
  };

  const handleFetchLogistics = async (orderId) => {
    setFetchingLogisticsId(orderId);
    try {
      const res = await crossBorderOrderService.fetchLogisticsInfo(orderId);
      if (res.status?.code === 200) {
        message.showToast("Logistics info fetched successfully", ToastTypes.SUCCESS);
        // Update the order in the list with new logistics data
        setCrossBorderOrders(prev => prev.map(order => {
          if (order._id === orderId) {
            const logisticsInfo = res.data.logisticsInfo?.[0];
            // Handle both array and object result structures
            const resultArray = Array.isArray(logisticsInfo?.result) ? logisticsInfo.result : (logisticsInfo?.result ? [logisticsInfo.result] : []);
            const firstLogistics = resultArray[0] || {};
            
            return {
              ...order,
              courierInfo: firstLogistics.logisticsBillNo && firstLogistics.logisticsCompanyName 
                ? `${firstLogistics.logisticsBillNo} (${firstLogistics.logisticsCompanyName})` 
                : (firstLogistics.name && firstLogistics.company ? `${firstLogistics.name} (${firstLogistics.company})` : order.courierInfo),
              logisticsInfo: res.data.logisticsInfo,
            };
          }
          return order;
        }));
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to fetch logistics", ToastTypes.ERROR);
    } finally {
      setFetchingLogisticsId(null);
    }
  };

  // Fetch order details for a single order
  const handleFetchOrderData = async (orderId) => {
    setFetchingOrderDataId(orderId);
    try {
      const res = await crossBorderOrderService.fetchOrderDetails(orderId);
      if (res.status?.code === 200) {
        message.showToast("Order data fetched successfully", ToastTypes.SUCCESS);
        // Refresh the list to show updated data
        fetchCrossBorderOrders();
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to fetch order data", ToastTypes.ERROR);
    } finally {
      setFetchingOrderDataId(null);
    }
  };

  // Fetch logistics trace for a single order
  const handleFetchTrace = async (orderId) => {
    setFetchingTraceId(orderId);
    try {
      const res = await crossBorderOrderService.fetchLogisticsTrace(orderId);
      if (res.status?.code === 200) {
        const deliveredMsg = res.data?.isDelivered ? " - Order Delivered!" : "";
        message.showToast(`Tracking fetched successfully${deliveredMsg}`, ToastTypes.SUCCESS);
        // Refresh the list to show updated data
        fetchCrossBorderOrders();
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.status?.message || error?.message || "Failed to fetch tracking";
      if (errorMsg.includes("No logistics ID")) {
        message.showToast("No tracking number available. Fetch logistics info first.", ToastTypes.WARNING);
      } else {
        message.showToast(errorMsg, ToastTypes.ERROR);
      }
    } finally {
      setFetchingTraceId(null);
    }
  };

  // Bulk fetch logistics for all orders on current page
  const handleBulkFetchLogistics = async () => {
    const ordersWithIds = crossBorderOrders.filter(o => o.alibabaOrderIds?.length > 0);
    if (ordersWithIds.length === 0) {
      message.showToast("No orders with Global IDs to fetch", ToastTypes.WARNING);
      return;
    }
    
    setIsBulkFetching(true);
    try {
      const orderIds = ordersWithIds.map(o => o._id);
      const res = await crossBorderOrderService.bulkFetchLogisticsInfo(orderIds);
      if (res.status?.code === 200) {
        message.showToast(res.status.message || "Bulk fetch completed", ToastTypes.SUCCESS);
        // Refresh the list to show updated data
        fetchCrossBorderOrders();
      }
    } catch (error) {
      message.showToast(error?.message || "Bulk fetch failed", ToastTypes.ERROR);
    } finally {
      setIsBulkFetching(false);
    }
  };

  // Bulk fetch order details (Global order data) for all orders on current page
  const handleBulkFetchOrderDetails = async () => {
    const ordersWithIds = crossBorderOrders.filter(o => o.alibabaOrderIds?.length > 0);
    if (ordersWithIds.length === 0) {
      message.showToast("No orders with Global IDs to fetch", ToastTypes.WARNING);
      return;
    }
    
    setIsBulkFetchingOrderDetails(true);
    let successCount = 0;
    let failCount = 0;
    
    try {
      for (const order of ordersWithIds) {
        try {
          const res = await crossBorderOrderService.fetchOrderDetails(order._id);
          if (res.status?.code === 200) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      message.showToast(`Order details fetched: ${successCount} success, ${failCount} failed`, ToastTypes.SUCCESS);
      // Refresh the list to show updated data
      fetchCrossBorderOrders();
    } catch (error) {
      message.showToast(error?.message || "Bulk fetch failed", ToastTypes.ERROR);
    } finally {
      setIsBulkFetchingOrderDetails(false);
    }
  };

  // Bulk fetch logistics trace for all orders on current page
  const handleBulkFetchTrace = async () => {
    const ordersWithLogistics = crossBorderOrders.filter(o => {
      // Check if order has logistics info with logisticsId
      const hasLogisticsId = o.logisticsInfo?.some(info => {
        const resultArray = Array.isArray(info?.result) ? info.result : (info?.result ? [info.result] : []);
        return resultArray.some(r => r?.logisticsId || r?.logisticsBillNo);
      });
      return o.alibabaOrderIds?.length > 0 && hasLogisticsId;
    });
    
    if (ordersWithLogistics.length === 0) {
      message.showToast("No orders with logistics info to fetch trace. Fetch logistics info first.", ToastTypes.WARNING);
      return;
    }
    
    setIsBulkFetchingTrace(true);
    let successCount = 0;
    let failCount = 0;
    let deliveredCount = 0;
    
    try {
      for (const order of ordersWithLogistics) {
        try {
          const res = await crossBorderOrderService.fetchLogisticsTrace(order._id);
          if (res.status?.code === 200) {
            successCount++;
            if (res.data?.isDelivered) {
              deliveredCount++;
            }
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      const deliveredMsg = deliveredCount > 0 ? ` (${deliveredCount} delivered)` : '';
      message.showToast(`Tracking fetched: ${successCount} success${deliveredMsg}, ${failCount} failed`, ToastTypes.SUCCESS);
      // Refresh the list to show updated data
      fetchCrossBorderOrders();
    } catch (error) {
      message.showToast(error?.message || "Bulk fetch failed", ToastTypes.ERROR);
    } finally {
      setIsBulkFetchingTrace(false);
    }
  };

  const fetchCrossBorderOrders = useCallback(async () => {
    setLoading(true);
    try {
      loader.showInlineLoader("Loading...", "medium");
      // Add status filter based on active tab
      const statusFilter = activeTab === "failed" ? "failed" : activeTab === "cancelled" ? "cancelled" : filters.status;
      const queryParams = `?page=${page}&limit=10&status=${statusFilter}&search=${filters.search}`;
      const res = await crossBorderOrderService.getAllCrossBorderOrders(queryParams);
      if (res.status?.code === 200) {
        const processedOrders = (res.data.orders || []).map(order => {
          const originalOrders = [...new Set(
            order.items?.map(item => item.originalUpsaleoOrderId).filter(Boolean)
          )];
          
          // Calculate total item quantity
          const totalQuantity = order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
          
          // Extract shipping and total from buyerOrderDetail (it's an array)
          // Get the first order detail result
          const orderDetailArray = Array.isArray(order.buyerOrderDetail) ? order.buyerOrderDetail : [order.buyerOrderDetail];
          const firstOrderDetail = orderDetailArray[0]?.result?.result || orderDetailArray[0]?.result || {};
          const baseInfo = firstOrderDetail?.baseInfo || {};
          
          // Get RMB values: prefer source order breakdown (AP_RMB) when stored, else 1688 API
          const itemTotalRmb = (order.sourceSumProductPayment != null && Number(order.sourceSumProductPayment) > 0)
            ? Number(order.sourceSumProductPayment)
            : (baseInfo?.sumProductPayment || firstOrderDetail?.sumProductPayment || 0);
          
          // Shipping fee in RMB
          const shippingFeeRmb = baseInfo?.shippingFee 
            || firstOrderDetail?.shippingFee 
            || firstOrderDetail?.logisticsOrderList?.[0]?.logisticsBillList?.[0]?.shippingFee
            || order.shippingFee 
            || 0;
          
          // Total amount in RMB: when using source item total, use itemTotal + shipping; else 1688 total
          const totalAmountRmb = (order.sourceSumProductPayment != null && Number(order.sourceSumProductPayment) > 0)
            ? (itemTotalRmb + shippingFeeRmb)
            : (baseInfo?.totalAmount || firstOrderDetail?.totalAmount || (itemTotalRmb + shippingFeeRmb));
          
          // Extract courier info from buyerOrderDetail or logisticsInfo
          let courierName = "";
          let parcelNo = "";
          
          // First try to get from logisticsInfo (from Global API)
          if (order.logisticsInfo?.length > 0) {
            const logisticsData = order.logisticsInfo[0];
            const resultArray = Array.isArray(logisticsData?.result) ? logisticsData.result : (logisticsData?.result ? [logisticsData.result] : []);
            const firstLogistics = resultArray[0] || {};
            courierName = firstLogistics.logisticsCompanyName || firstLogistics.company || "";
            parcelNo = firstLogistics.logisticsBillNo || firstLogistics.name || "";
          }
          
          // Fallback to buyerOrderDetail if no logisticsInfo
          if (!courierName && !parcelNo) {
            const logisticsInfo = firstOrderDetail?.nativeLogistics?.logisticsItems?.[0] 
              || firstOrderDetail?.logisticsOrderList?.[0]?.logisticsBillList?.[0]
              || {};
            courierName = logisticsInfo.logisticsCompanyName || logisticsInfo.cpCode || order.courierName || "";
            parcelNo = logisticsInfo.logisticsBillNo || logisticsInfo.logisticsId || order.parcelNo || "";
          }
          
          // Get actual status from 1688 API response
          const apiStatus = firstOrderDetail?.baseInfo?.status 
            || firstOrderDetail?.status 
            || firstOrderDetail?.orderStatus
            || orderDetailArray[0]?.result?.baseInfo?.status
            || null;
          
          // Check tracking history to determine status and get latest tracking event
          let derivedStatus = apiStatus;
          let latestTrackingEvent = null;
          let hasTrackingInfo = false;
          
          if (order.logisticsTrace?.length > 0) {
            for (const trace of order.logisticsTrace) {
              const traceArray = trace?.logisticsTrace || [];
              for (const traceItem of traceArray) {
                const steps = traceItem?.logisticsSteps || [];
                if (steps.length > 0) {
                  hasTrackingInfo = true;
                  // Get the latest tracking event (last in array)
                  const latestStep = steps[steps.length - 1];
                  if (!latestTrackingEvent || new Date(latestStep.acceptTime) > new Date(latestTrackingEvent.acceptTime)) {
                    latestTrackingEvent = latestStep;
                  }
                  
                  // Check if delivered
                  const isDelivered = steps.some(step => {
                    const remark = (step.remark || step.desc || '').toLowerCase();
                    return remark.includes('delivered') || 
                           remark.includes('已签收') || 
                           remark.includes('has been delivered') ||
                           remark.includes('签收') ||
                           remark.includes('recipient');
                  });
                  if (isDelivered) {
                    derivedStatus = 'delivered';
                  }
                }
              }
            }
            
            // If has tracking but not delivered, show "in_transit"
            if (hasTrackingInfo && derivedStatus !== 'delivered') {
              derivedStatus = 'in_transit';
            }
          }
          
          // Extract order reference from message (e.g., "必须在快递单上备注：YK0001" -> "YK0001")
          // Try multiple patterns to extract the reference
          let orderRef = '';
          const message = order.message || '';
          
          // Pattern 1: After colon (Chinese or English)
          const colonMatch = message.match(/[：:]\s*([A-Z]{2,}[0-9]+)/i);
          if (colonMatch) {
            orderRef = colonMatch[1];
          } else {
            // Pattern 2: Any alphanumeric code like YK0001, AB1234
            const codeMatch = message.match(/\b([A-Z]{2,}[0-9]+)\b/i);
            if (codeMatch) {
              orderRef = codeMatch[1];
            }
          }
          
          return {
            ...order,
            chinaNo: orderRef || '-',
            globalOrderId: order.alibabaOrderIds?.join(", ") || "-",
            courierInfo: courierName && parcelNo ? `${parcelNo} (${courierName})` : (parcelNo || courierName || "-"),
            routeCode: order.routeCode || "-",
            originalOrders: originalOrders,
            itemCount: order.items?.length || 0,
            totalQuantity: totalQuantity,
            supplierMessage: order.message || "-",
            itemTotal: itemTotalRmb,
            shippingFee: shippingFeeRmb,
            totalAmount: totalAmountRmb,
            apiStatus: derivedStatus, // Status derived from tracking history or 1688 API
            latestTrackingEvent: latestTrackingEvent, // Latest tracking event for tooltip
          };
        });
        
        setCrossBorderOrders(processedOrders);
        setPages({ 
          currentPage: res.data.currentPage, 
          totalPages: res.data.totalPages, 
          totalItems: res.data.totalOrders,
          failedCount: res.data.failedCount || 0,
          cancelledCount: res.data.cancelledCount || 0
        });

        // Translate supplier names
        translateSupplierNames(processedOrders);
        translateVariants(processedOrders);
      }
    } catch (error) {
      message.showToast(error?.message || "Failed to fetch", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
      setLoading(false);
      setInitialLoading(false);
    }
  }, [page, filters, activeTab]);

  useEffect(() => {
    fetchCrossBorderOrders();
  }, [fetchCrossBorderOrders]);

  const getStatusBadge = (status, latestTrackingEvent = null) => {
    // Map 1688 API status values to display config
    const statusLower = String(status || '').toLowerCase();
    const config = {
      // Internal statuses
      'pending': { cls: 'status-pending', icon: 'fa-clock', label: 'Pending' },
      'processing': { cls: 'status-processing', icon: 'fa-cog', label: 'Processing' },
      'paid': { cls: 'status-paid', icon: 'fa-check', label: 'Paid' },
      'shipped': { cls: 'status-shipped', icon: 'fa-truck', label: 'Shipped' },
      'in_transit': { cls: 'status-intransit', icon: 'fa-shipping-fast', label: 'In Transit' },
      'delivered': { cls: 'status-delivered', icon: 'fa-check-circle', label: 'Delivered' },
      'failed': { cls: 'status-failed', icon: 'fa-exclamation-triangle', label: 'Failed' },
      // 1688 API statuses
      'waitbuyerpay': { cls: 'status-pending', icon: 'fa-clock', label: 'Wait Buyer Pay' },
      'waitsellersend': { cls: 'status-processing', icon: 'fa-box', label: 'Wait Seller Send' },
      'waitbuyerreceive': { cls: 'status-shipped', icon: 'fa-truck', label: 'Wait Buyer Receive' },
      'confirm_goods': { cls: 'status-delivered', icon: 'fa-check-circle', label: 'Confirmed' },
      'success': { cls: 'status-delivered', icon: 'fa-check-circle', label: 'Success' },
      'cancel': { cls: 'status-failed', icon: 'fa-times-circle', label: 'Cancelled' },
      'terminated': { cls: 'status-failed', icon: 'fa-ban', label: 'Terminated' },
      'waitselleract': { cls: 'status-processing', icon: 'fa-hourglass-half', label: 'Wait Seller Act' },
      'waitbuyeract': { cls: 'status-pending', icon: 'fa-user-clock', label: 'Wait Buyer Act' },
    }[statusLower] || { cls: 'status-default', icon: 'fa-circle', label: status || 'N/A' };
    
    // Build tooltip with latest tracking event
    let tooltipText = '';
    if (latestTrackingEvent) {
      const time = latestTrackingEvent.acceptTime || '';
      const remark = latestTrackingEvent.remark || latestTrackingEvent.desc || '';
      // Truncate remark if too long
      const shortRemark = remark.length > 100 ? remark.substring(0, 100) + '...' : remark;
      tooltipText = `${time}\n${shortRemark}`;
    }
    
    return (
      <span 
        className={`cb-status-badge ${config.cls}`} 
        title={tooltipText}
        style={{ cursor: latestTrackingEvent ? 'help' : 'default' }}
      >
        <i className={`fas ${config.icon}`}></i>
        {config.label}
      </span>
    );
  };

  // Get translated or original supplier name
  const getSupplierName = (name) => {
    if (!name) return "-";
    return translatedNames[name] || name;
  };

  // ==========================================
  // ORIGINAL DISPLAY COLUMNS (FOR SCREEN ONLY)
  // ==========================================
  const displayColumns = [
    { 
      key: "chinaNo", 
      label: "China No", 
      width: 100, 
      render: (v, row) => (
        <span 
          className="order-id-cell cell-link" 
          onClick={() => window.open(`/admin/cross-border-orders/${row._id}`, '_blank')}
          style={{ cursor: 'pointer', color: '#1890ff', fontWeight: 500 }}
        >
          {v || "-"}
        </span>
      )
    },
    { 
      key: "globalOrderId", 
      label: "Global Order ID", 
      width: 180, 
      render: (v, row) => {
        if (row.status === 'failed') {
          let errorMsg = row.apiError;
          if (!errorMsg && row.apiResponse) {
            errorMsg = row.apiResponse?.message 
              || row.apiResponse?.result?.message 
              || row.apiResponse?.errorMessage 
              || row.apiResponse?.result?.errorMessage
              || row.apiResponse?.error
              || row.apiResponse?.result?.error;
          }
          errorMsg = errorMsg || "Order creation failed";
          return (
            <span 
              className="error-message-cell" 
              title={errorMsg}
              style={{ color: '#ff4d4f', fontSize: '12px', cursor: 'help' }}
            >
              {errorMsg.length > 40 ? errorMsg.substring(0, 40) + '...' : errorMsg}
            </span>
          );
        }
        return <span>{v || "-"}</span>;
      }
    },
    { 
      key: "courierInfo", 
      label: "Courier Name & Parcel No", 
      width: 200, 
      render: (v) => <span>{v || "-"}</span>
    },
    { 
      key: "routeCode", 
      label: "Route Code", 
      width: 100, 
      render: (v) => <span>{v || "-"}</span>
    },
    { 
      key: "supplierName", 
      label: "Supplier Name", 
      width: 200,
      wrap: true,
      render: (v) => {
        const translatedName = translatedNames[v];
        const hasChinese = v && /[\u4e00-\u9fa5]/.test(v);
        
        if (hasChinese && translatedName && translatedName !== v) {
          return (
            <div className="supplier-name-cell">
              <span className="supplier-english">{translatedName}</span>
              <span className="supplier-chinese">{v}</span>
            </div>
          );
        }
        return <span className="supplier-name-cell">{v || "-"}</span>;
      }
    },
    { 
      key: "itemCount", 
      label: "Items", 
      width: 80, 
      render: (v) => `${v || 0} SKUs` 
    },
    { 
      key: "totalQuantity", 
      label: "Item Quantity", 
      width: 100, 
      render: (v) => v || 0
    },
    { 
      key: "supplierMessage", 
      label: "Supplier Message", 
      width: 200,
      wrap: true,
      render: (v) => (
        <span className="comment-cell-full" title={v}>{v || "-"}</span>
      )
    },
    { 
      key: "itemTotal", 
      label: "Item Total", 
      width: 100, 
      render: (v) => v ? `¥ ${v.toFixed(2)}` : "¥ 0.00"
    },
    { 
      key: "shippingFee", 
      label: "Shipping", 
      width: 90, 
      render: (v) => v ? `¥ ${v.toFixed(2)}` : "¥ 0.00"
    },
    { 
      key: "totalAmount", 
      label: "Total", 
      width: 100, 
      render: (v) => v ? `¥ ${v.toFixed(2)}` : "¥ 0.00"
    },
    { 
      key: "originalOrders", 
      label: "Original Order ID", 
      width: 180, 
      render: (v) => {
        if (!v || v.length === 0) return "-";
        const displayText = v.join(", ");
        return (
          <span className="original-orders-cell" title={displayText}>
            {displayText}
          </span>
        );
      }
    },
    { 
      key: "status", 
      label: "Status", 
      width: 120, 
      render: (v, row) => {
        // Use actual API status if available, otherwise fallback to internal status
        const displayStatus = row.apiStatus || v;
        return getStatusBadge(displayStatus, row.latestTrackingEvent);
      }
    },
    { 
      key: "createdAt", 
      label: "Created", 
      width: 140, 
      render: (v) => v ? moment(v).format("DD MMM YY, HH:mm") : "-" 
    },
    {
      key: "actions",
      label: "Actions",
      width: 280,
      render: (v, row) => {
        const hasAlibabaIds = row.alibabaOrderIds?.length > 0;
        const hasLogisticsId = row.logisticsInfo?.some(info => {
          const resultArray = Array.isArray(info?.result) ? info.result : (info?.result ? [info.result] : []);
          return resultArray.some(r => r?.logisticsId || r?.logisticsBillNo);
        });
        
        return (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <button
              onClick={(e) => { e.stopPropagation(); handleFetchLogistics(row._id); }}
              disabled={fetchingLogisticsId === row._id || !hasAlibabaIds}
              style={{
                padding: '3px 6px',
                fontSize: '10px',
                borderRadius: '4px',
                border: 'none',
                background: hasAlibabaIds ? '#10b981' : '#d1d5db',
                color: '#fff',
                cursor: hasAlibabaIds ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
              }}
              title="Fetch logistics info"
            >
              <i className={`fas ${fetchingLogisticsId === row._id ? 'fa-spinner fa-spin' : 'fa-truck'}`} style={{ fontSize: '9px' }}></i>
              {fetchingLogisticsId === row._id ? '...' : 'Logistics'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleFetchOrderData(row._id); }}
              disabled={fetchingOrderDataId === row._id || !hasAlibabaIds}
              style={{
                padding: '3px 6px',
                fontSize: '10px',
                borderRadius: '4px',
                border: 'none',
                background: hasAlibabaIds ? '#4361ee' : '#d1d5db',
                color: '#fff',
                cursor: hasAlibabaIds ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
              }}
              title="Fetch order data from Global API"
            >
              <i className={`fas ${fetchingOrderDataId === row._id ? 'fa-spinner fa-spin' : 'fa-sync'}`} style={{ fontSize: '9px' }}></i>
              {fetchingOrderDataId === row._id ? '...' : 'Order Data'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleFetchTrace(row._id); }}
              disabled={fetchingTraceId === row._id || !hasAlibabaIds || !hasLogisticsId}
              style={{
                padding: '3px 6px',
                fontSize: '10px',
                borderRadius: '4px',
                border: 'none',
                background: (hasAlibabaIds && hasLogisticsId) ? '#f59e0b' : '#d1d5db',
                color: '#fff',
                cursor: (hasAlibabaIds && hasLogisticsId) ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
              }}
              title={!hasLogisticsId ? "Fetch logistics info first" : "Fetch tracking info"}
            >
              <i className={`fas ${fetchingTraceId === row._id ? 'fa-spinner fa-spin' : 'fa-map-marker-alt'}`} style={{ fontSize: '9px' }}></i>
              {fetchingTraceId === row._id ? '...' : 'Tracking'}
            </button>
          </div>
        );
      }
    },
  ];

  // ==========================================
  // NATIVE CSV EXPORT FUNCTION (MULTI-ROW FOR SKUs)
  // ==========================================
  const handleExportExcel = () => {
    if (!crossBorderOrders || crossBorderOrders.length === 0) {
      message.showToast("No data to export", ToastTypes.WARNING);
      return;
    }

    const headers = [
      "China S. No.", "Product China S. No.", "India S. No.", "Entry Date", "Order ID", 
      "Prod Name", "URL", "Qty", "Pc/Set", "Variant", "Chinese Variant", 
      "Unit Price", "Qty Price", "Local Shipping", "Total Price"
    ];

    const excelRows = [];

    crossBorderOrders.forEach((row) => {
      const chinaSNo = row.chinaNo || "-";
      const entryDate = row.createdAt ? moment(row.createdAt).format("DD-MM-YYYY") : "-";
      const orderId = row.originalOrders?.join(", ") || "-";
      const orderTotalQty = row.totalQuantity || 0;

      const orderDetailArray = Array.isArray(row.buyerOrderDetail) ? row.buyerOrderDetail : [row.buyerOrderDetail];
      const firstOrderDetail = orderDetailArray[0]?.result?.result || orderDetailArray[0]?.result || {};
      const productItems = firstOrderDetail?.productItems || [];

      // If an order somehow has no items, create an empty fallback row
      if (!row.items || row.items.length === 0) {
        excelRows.push([
          chinaSNo, "-", "", entryDate, orderId, "", "-", 
          orderTotalQty, "pcs", "-", "-", "¥ 0.00", "¥ 0.00", 
          `¥ ${(row.shippingFee || 0).toFixed(2)}`, `¥ ${(row.shippingFee || 0).toFixed(2)}`
        ]);
      } else {
        // Iterate over each SKU and create a dedicated row
        row.items.forEach((item, itemIndex) => {
          
          // 1. Calculate Product China S. No. (e.g. OrderRef-A-2, OrderRef-B-1)
          const skuLetter = String.fromCharCode(65 + itemIndex);
          const itemQty = item.quantity || 1;
          const baseOrderRef = chinaSNo !== "-" ? chinaSNo : "CB";
          // Leave Product China S. No. blank if it's a one-SKU order
          const productChinaNo = row.items.length === 1 ? "" : `${baseOrderRef}-${skuLetter}-${itemQty}`;

          // 2. URL
          const url = item.offerId ? `https://detail.1688.com/offer/${item.offerId}.html` : "-";

          // 3. Extract & Format Variants
          const matchingProduct = productItems.find(p => p.specId === item.specId || p.productID === item.offerId);
          const baseVariantCn = matchingProduct?.skuInfos?.map(s => `${s.name}: ${s.value}`).join(', ') || '';
          
          // Fetch English mapping if available
          const baseVariantEn = translatedVariants[baseVariantCn] || baseVariantCn;

          // Variants will just be the raw text (Item Qty has been removed)
          const variantCn = baseVariantCn || "-";
          const variantEn = baseVariantEn || "-";

          // 4. Calculate Prices
          const unitPriceRaw = item.price || 0;
          const unitPrice = `¥ ${unitPriceRaw.toFixed(2)}`;

          const qtyPriceRaw = unitPriceRaw * itemQty;
          const qtyPrice = `¥ ${qtyPriceRaw.toFixed(2)}`;

          // 5. Local Shipping (First SKU gets the fee, subsequent SKUs get 0)
          const localShippingRaw = itemIndex === 0 ? (row.shippingFee || 0) : 0;
          const localShipping = `¥ ${localShippingRaw.toFixed(2)}`;

          // 6. Total Price for this specific SKU row
          const totalPriceRaw = qtyPriceRaw + localShippingRaw;
          const totalPrice = `¥ ${totalPriceRaw.toFixed(2)}`;

          // Append to array
          excelRows.push([
            chinaSNo,          // China S. No.
            productChinaNo,    // Product China S. No.
            "",                // India S. No.
            entryDate,         // Entry Date
            orderId,           // Order ID
            "",                // Prod Name
            url,               // URL
            itemQty,           // Qty 
            "pcs",             // Pc/Set (Native CSV dropdown fallback)
            variantEn,         // Variant (English)
            variantCn,         // Chinese Variant
            unitPrice,         // Unit Price
            qtyPrice,          // Qty Price
            localShipping,     // Local Shipping
            totalPrice         // Total Price
          ]);
        });
      }
    });

    // Native CSV string construction
    const csvContent = [
      headers.join(","),
      ...excelRows.map(row => 
        row.map(cell => {
          const cellStr = String(cell);
          // Escape quotes and wrap cell in quotes to safely handle any commas inside the data
          return `"${cellStr.replace(/"/g, '""')}"`;
        }).join(",")
      )
    ].join("\n");

    // Adding \uFEFF (Byte Order Mark) forces Excel to read the CSV in UTF-8
    // This perfectly preserves Chinese Characters and the Yen (¥) symbol
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const urlString = URL.createObjectURL(blob);
    
    link.setAttribute("href", urlString);
    link.setAttribute("download", `CrossBorder_Orders_${moment().format("YYYYMMDD_HHmm")}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toolbarLeftActions = (
    <div className="cb-toolbar-left">
      <select 
        className="cb-toolbar-select"
        value={filters.status} 
        onChange={(e) => { 
          setFilters(p => ({ ...p, status: e.target.value })); 
          setPage(1); 
        }}
      >
        <option value="">All Status</option>
        <option value="pending">Pending</option>
        <option value="processing">Processing</option>
        <option value="delivered">Delivered</option>
        <option value="failed">Failed</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <input 
        className="cb-toolbar-input"
        placeholder="Search by Order ID, Supplier..." 
        value={filters.search} 
        onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))} 
        onKeyDown={(e) => e.key === "Enter" && fetchCrossBorderOrders()}
      />
      <button className="cb-toolbar-btn" onClick={fetchCrossBorderOrders}>
        <i className="fas fa-search"></i> Search
      </button>

      {/* Manual Export Button running the Native CSV logic */}
      <button 
        className="cb-toolbar-btn" 
        onClick={handleExportExcel}
        style={{ background: '#217346', borderColor: '#217346', color: 'white', marginLeft: '8px' }}
        title="Export Current Page to Excel (CSV)"
      >
        <i className="fas fa-file-excel"></i> Export Excel
      </button>

      <button 
        className="cb-toolbar-btn" 
        onClick={handleBulkFetchLogistics}
        disabled={isBulkFetching || isBulkFetchingOrderDetails || isBulkFetchingTrace}
        style={{ background: '#10b981', borderColor: '#10b981', marginLeft: '8px' }}
        title="Fetch logistics info for all orders on current page"
      >
        <i className={`fas ${isBulkFetching ? 'fa-spinner fa-spin' : 'fa-truck'}`}></i> 
        {isBulkFetching ? 'Fetching...' : 'Fetch Logistics (Page)'}
      </button>
      <button 
        className="cb-toolbar-btn" 
        onClick={handleBulkFetchOrderDetails}
        disabled={isBulkFetching || isBulkFetchingOrderDetails || isBulkFetchingTrace}
        style={{ background: '#4361ee', borderColor: '#4361ee' }}
        title="Fetch order details from Global API for all orders on current page"
      >
        <i className={`fas ${isBulkFetchingOrderDetails ? 'fa-spinner fa-spin' : 'fa-sync'}`}></i> 
        {isBulkFetchingOrderDetails ? 'Fetching...' : 'Fetch Order Data (Page)'}
      </button>
      <button 
        className="cb-toolbar-btn" 
        onClick={handleBulkFetchTrace}
        disabled={isBulkFetching || isBulkFetchingOrderDetails || isBulkFetchingTrace}
        style={{ background: '#f59e0b', borderColor: '#f59e0b' }}
        title="Fetch tracking info for all orders on current page (requires logistics info first)"
      >
        <i className={`fas ${isBulkFetchingTrace ? 'fa-spinner fa-spin' : 'fa-map-marker-alt'}`}></i> 
        {isBulkFetchingTrace ? 'Fetching...' : 'Fetch Tracking (Page)'}
      </button>
    </div>
  );

  // Get failed and cancelled counts from API response (stored in pages state)
  const failedCount = pages.failedCount || 0;
  const cancelledCount = pages.cancelledCount || 0;

  return (
    <div className="cb-container">
      <div className="cb-content">
        
        {/* Tabs for All Orders, Failed Orders, and Cancelled Orders */}
        <div className="cb-tabs" style={{ display: 'flex', gap: '0', marginBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
          <button
            onClick={() => { setActiveTab("all"); setPage(1); }}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: activeTab === "all" ? 600 : 400,
              border: 'none',
              borderBottom: activeTab === "all" ? '2px solid #4361ee' : '2px solid transparent',
              marginBottom: '-2px',
              background: 'transparent',
              color: activeTab === "all" ? '#4361ee' : '#6b7280',
              cursor: 'pointer',
            }}
          >
            All Orders
          </button>
          <button
            onClick={() => { setActiveTab("failed"); setPage(1); }}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: activeTab === "failed" ? 600 : 400,
              border: 'none',
              borderBottom: activeTab === "failed" ? '2px solid #dc2626' : '2px solid transparent',
              marginBottom: '-2px',
              background: 'transparent',
              color: activeTab === "failed" ? '#dc2626' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Failed Orders
            {activeTab !== "failed" && failedCount > 0 && (
              <span style={{
                background: '#dc2626',
                color: '#fff',
                fontSize: '11px',
                padding: '2px 6px',
                borderRadius: '10px',
                fontWeight: 500,
              }}>
                {failedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab("cancelled"); setPage(1); }}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: activeTab === "cancelled" ? 600 : 400,
              border: 'none',
              borderBottom: activeTab === "cancelled" ? '2px solid #f59e0b' : '2px solid transparent',
              marginBottom: '-2px',
              background: 'transparent',
              color: activeTab === "cancelled" ? '#f59e0b' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Cancelled Orders
            {activeTab !== "cancelled" && cancelledCount > 0 && (
              <span style={{
                background: '#f59e0b',
                color: '#fff',
                fontSize: '11px',
                padding: '2px 6px',
                borderRadius: '10px',
                fontWeight: 500,
              }}>
                {cancelledCount}
              </span>
            )}
          </button>
        </div>
        
        <ExcelTable 
          data={crossBorderOrders} 
          columns={displayColumns}
          isLoading={initialLoading || loading}
          loadingText="Loading orders..."
          title={
            activeTab === "failed" 
              ? `Failed Orders (${pages.totalItems || 0})` 
              : activeTab === "cancelled"
              ? `Cancelled Orders (${pages.totalItems || 0})`
              : `Cross-Border Orders (${pages.totalItems || 0})`
          }
          sortable 
          resizable 
          showToolbar 
          showToolbarExport={false} 
          showToolbarColumns 
          showToolbarFullscreen
          toolbarLeftActions={toolbarLeftActions}
        />
        
        {crossBorderOrders.length > 0 && (
          <div className="pagination-section" style={{ marginTop: '16px' }}>
            <Pagination pages={pages} page={page} setPage={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
